import React, { useMemo, useRef, useState } from "react";
import {
  Badge,
  Box,
  Button,
  Flex,
  Heading,
  HStack,
  Input,
  Spinner,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tooltip,
  Tr,
  VStack,
} from "@chakra-ui/react";
import { Navigate } from "react-router-dom";
import PageScaffold from "../../components/PageScaffold";
import paths from "../../utils/paths";
import { useUserDetailsContext } from "../../context/userDetailsContext";
import services from "../Orders/services";
import { canAccessOrderLifecycle } from "./utils/canAccessOrderLifecycle";
import {
  isOrcaUserDetailsReady,
  resolveUserSubType,
} from "./utils/getStoredOrcaUser";
import {
  buildLifecycleSteps,
  EstimateApiRow,
  LifecycleApiRow,
  PartConfirmationApiRow,
  ShipmentApiRow,
  SupplierAllocationApiRow,
} from "./buildLifecycleSteps";

type LifecycleResult = {
  searchedAs: "ENQUIRY" | "CONFIRMED_ORDER";
  searchedOrderNo?: string;
  enquiryOrderNo: string;
  rows: LifecycleApiRow[];
  partConfirmations: PartConfirmationApiRow[];
  supplierAllocations?: SupplierAllocationApiRow[];
  estimates?: EstimateApiRow[];
  shipments?: ShipmentApiRow[];
};

const ControlTower = () => {
  const userDetails = useUserDetailsContext();
  // Wait / use cached ORCA_USER so we never gate on the empty initial context.
  const isUserDetailsReady = isOrcaUserDetailsReady(userDetails);
  const canAccess = canAccessOrderLifecycle(resolveUserSubType(userDetails));

  const [searchInput, setSearchInput] = useState("");
  const [isSearching, setIsSearching] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<LifecycleResult | null>(null);
  const searchRequestIdRef = useRef(0);

  const focusRow = useMemo(() => {
    if (!result?.rows?.length) return null;
    return (
      result.rows.find((r) => r.kind === "CONFIRMED_ORDER") || result.rows[0]
    );
  }, [result]);

  const steps = useMemo(() => {
    if (!focusRow) return [];
    return buildLifecycleSteps(
      focusRow,
      result?.partConfirmations,
      result?.supplierAllocations,
      result?.estimates,
      result?.shipments,
    );
  }, [
    focusRow,
    result?.partConfirmations,
    result?.supplierAllocations,
    result?.estimates,
    result?.shipments,
  ]);

  if (!isUserDetailsReady) {
    return (
      <PageScaffold title="Control Tower" isHeaderVisible>
        <Flex justify="center" py={16}>
          <Spinner />
        </Flex>
      </PageScaffold>
    );
  }

  if (!canAccess) {
    return <Navigate to={paths.home} replace />;
  }

  const handleSearch = async () => {
    const orderNo = searchInput.trim();
    if (!orderNo) {
      setError("Enter an order number.");
      return;
    }

    const requestId = ++searchRequestIdRef.current;
    setIsSearching(true);
    setError(null);
    setResult(null);

    try {
      const data = await services.getOrderLifecycle(orderNo);
      if (requestId !== searchRequestIdRef.current) return;
      setResult(data as LifecycleResult);
    } catch (err: any) {
      if (requestId !== searchRequestIdRef.current) return;
      const status = err?.response?.status;
      const rawMessage = err?.response?.data?.message;
      const apiMessage = Array.isArray(rawMessage)
        ? rawMessage.filter((m) => typeof m === "string").join(" ")
        : typeof rawMessage === "string"
          ? rawMessage.trim()
          : "";

      if (status === 404) {
        setError("Order number is not correct.");
      } else if (status === 400) {
        setError(apiMessage || "Search by order number only.");
      } else {
        setError(apiMessage || "Failed to load lifecycle data.");
      }
    } finally {
      if (requestId === searchRequestIdRef.current) {
        setIsSearching(false);
      }
    }
  };

  return (
    <PageScaffold title="Control Tower" isHeaderVisible>
      <Box maxW="960px" mx="auto" px={4} py={6}>
        <VStack align="stretch" spacing={4}>
          <Text color="gray.600" fontSize="sm">
            Search by order number
          </Text>

          <Box
            as="form"
            onSubmit={(e: React.FormEvent) => {
              e.preventDefault();
              void handleSearch();
            }}
            display="flex"
            gap={3}
          >
            <Input
              value={searchInput}
              onChange={(e) => {
                const nextValue = e.target.value;
                setSearchInput(nextValue);
                if (!nextValue.trim()) {
                  // Invalidate in-flight searches so stale setResult cannot revive.
                  searchRequestIdRef.current += 1;
                  setIsSearching(false);
                  setResult(null);
                  setError(null);
                }
              }}
              placeholder="Order number"
              bg="white"
              maxW="420px"
            />
            <Button colorScheme="blue" type="submit" isLoading={isSearching}>
              Search
            </Button>
          </Box>

          {error ? (
            <Text color="red.500" fontSize="sm">
              {error}
            </Text>
          ) : null}

          {isSearching ? (
            <Flex justify="center" py={8}>
              <Spinner />
            </Flex>
          ) : null}

          {result && focusRow ? (
            <VStack align="stretch" spacing={5}>
              <Box
                borderWidth="1px"
                borderColor="gray.200"
                borderRadius="md"
                bg="white"
                px={4}
                py={3}
              >
                <HStack spacing={3} flexWrap="wrap">
                  <Heading size="sm">{focusRow.orderNo}</Heading>
                  <Badge colorScheme="gray">
                    {focusRow.kind === "ENQUIRY"
                      ? "Enquiry"
                      : "Confirmed order"}
                  </Badge>
                  <Badge>{focusRow.orderStatus || "—"}</Badge>
                </HStack>
                <Text fontSize="sm" color="gray.600" mt={1}>
                  {focusRow.customerName || "—"} · Handler:{" "}
                  {focusRow.orderHandlerName || "—"}
                  {result.enquiryOrderNo
                    ? ` · Enquiry: ${result.enquiryOrderNo}`
                    : ""}
                </Text>
              </Box>

              <Box
                borderWidth="1px"
                borderColor="gray.200"
                borderRadius="md"
                bg="white"
                overflowX="auto"
              >
                <Table size="sm" variant="simple">
                  <Thead bg="gray.50">
                    <Tr>
                      <Th>Stage</Th>
                      <Th>Time</Th>
                      <Th>Person</Th>
                      <Th maxW="120px" whiteSpace="normal" lineHeight="1.3">
                        Diff from previous stage
                      </Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {steps.length === 0 ? (
                      <Tr>
                        <Td colSpan={4}>
                          <Text color="gray.500" fontSize="sm">
                            No timed lifecycle steps recorded yet.
                          </Text>
                        </Td>
                      </Tr>
                    ) : (
                      steps.map((step) => (
                        <Tr key={step.id}>
                          <Td fontWeight="medium">
                            {step.statusTooltip ? (
                              <Tooltip
                                label={step.statusTooltip}
                                hasArrow
                                placement="top-start"
                                openDelay={200}
                              >
                                <Box as="span" cursor="help">
                                  {step.status}
                                </Box>
                              </Tooltip>
                            ) : (
                              step.status
                            )}
                          </Td>
                          <Td whiteSpace="nowrap">{step.at}</Td>
                          <Td>{step.person}</Td>
                          <Td whiteSpace="nowrap">{step.diffFromPrevious}</Td>
                        </Tr>
                      ))
                    )}
                  </Tbody>
                </Table>
              </Box>
            </VStack>
          ) : null}
        </VStack>
      </Box>
    </PageScaffold>
  );
};

export default ControlTower;
